import { FieldArray, useField, useFormikContext } from 'formik'
import { useTranslation } from 'react-i18next'
import { TextFieldInput } from '@dhl/formik'
import { ActionUpdate, Answer, LanguageString, QuizSetup } from '@dhl/receiver-dhl-extra-api'
import { DeleteIcon, PlusIcon } from '@dhl-ui/icons'
import { Body2, Box, Button, Card, Grid, H5, Stack } from '@dhl-ui/components'

const getDefaultLanguageValues = (): LanguageString[] => [
  { language: 'en', value: '' },
  { language: 'nl', value: '' },
]

const answerKeyPrefix = 'answer-'

export const defaultQuizFields: QuizSetup = {
  question: getDefaultLanguageValues(),
  answers: [
    {
      key: `${answerKeyPrefix}1`,
      values: getDefaultLanguageValues(),
    },
    {
      key: `${answerKeyPrefix}2`,
      values: getDefaultLanguageValues(),
    },
  ],
}

type Props = {
  name: string
}

export const QuizInput = ({ name }: Props) => {
  const { t } = useTranslation()
  const { setFieldValue } = useFormikContext<ActionUpdate>()
  const [{ value: quiz }] = useField<QuizSetup>(name)

  const handleRemoveAnswer = (removeIndex: number) => {
    const { answers = [] } = quiz || {}
    answers.splice(removeIndex, 1)
    const nextAnswers = answers
      .filter((_, index) => index !== removeIndex)
      .map((answer, index) => ({
        ...answer,
        key: `${answerKeyPrefix}${index + 1}`,
      }))
    setFieldValue(`${name}.answers`, nextAnswers)
  }

  const handleAddAnswer = () => {
    const { answers = [] } = quiz || {}
    const nextAnswers: Answer[] = [
      ...answers,
      {
        key: `${answerKeyPrefix}${answers.length + 1}`,
        values: getDefaultLanguageValues(),
      },
    ]
    setFieldValue(`${name}.answers`, nextAnswers)
  }

  return (
    <Grid>
      <Card>
        <Card.Content>
          <H5>{t('admin.dhlExtraPage.promotionsPage.promotionForm.quiz.title')}</H5>
          <Grid container spacing={2} mt={0}>
            <Grid size={{ xs: 12 }} mt={2}>
              <Body2>{t('admin.dhlExtraPage.promotionsPage.promotionForm.quiz.question')}</Body2>
            </Grid>
            <FieldArray
              name={`${name}.question`}
              render={() =>
                quiz?.question?.map((question, index) => {
                  const { language } = question
                  return (
                    <Grid size={{ xs: 6 }} key={`question-${index}`}>
                      <TextFieldInput
                        fullWidth
                        label={t('admin.dhlExtraPage.promotionsPage.promotionForm.quiz.questionFor', {
                          lang: language.toUpperCase(),
                        })}
                        name={`${name}.question.${index}.value`}
                        type="text"
                      />
                    </Grid>
                  )
                })
              }
            />
            <Grid size={{ xs: 12 }}>
              <Body2>{t('admin.dhlExtraPage.promotionsPage.promotionForm.quiz.answers')}</Body2>
            </Grid>
            <FieldArray
              name={`${name}.answers`}
              render={() => {
                return (
                  <>
                    {quiz?.answers?.map((answer, answerIndex) => (
                      <FieldArray
                        name={`${name}.answer.${answerIndex}`}
                        key={`answer-${answerIndex}`}
                        render={() =>
                          answer?.values?.map((value, index) => {
                            const { language } = value
                            return (
                              <Grid size={{ xs: 6 }} key={`answer-${answerIndex}-${index}`}>
                                <Stack direction="row" display="flex" alignItems="center">
                                  {index === 0 && (
                                    <Box sx={{ mr: 2, lineHeight: '48px' }}>
                                      <Body2>{answerIndex + 1}</Body2>
                                    </Box>
                                  )}
                                  <TextFieldInput
                                    fullWidth
                                    label={t('admin.dhlExtraPage.promotionsPage.promotionForm.quiz.answerFor', {
                                      lang: language.toUpperCase(),
                                    })}
                                    name={`${name}.answers.${answerIndex}.values.${index}.value`}
                                    type="text"
                                  />
                                  {index === 1 && (quiz?.answers?.length ?? 0) > 2 && (
                                    <Button
                                      variant="text"
                                      sx={{ ml: 2, mt: 0.75 }}
                                      onClick={() => handleRemoveAnswer(answerIndex)}
                                    >
                                      <DeleteIcon />
                                    </Button>
                                  )}
                                </Stack>
                              </Grid>
                            )
                          })
                        }
                      />
                    ))}
                    <Grid>
                      <Button variant="outlined" startIcon={<PlusIcon />} onClick={handleAddAnswer}>
                        {t('admin.dhlExtraPage.promotionsPage.promotionForm.quiz.addAnswerButton')}
                      </Button>
                    </Grid>
                  </>
                )
              }}
            />
          </Grid>
        </Card.Content>
      </Card>
    </Grid>
  )
}
